import React, { useState, useContext, useMemo, useCallback, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  useTheme,
  useMediaQuery,
  Avatar
} from "@mui/material";
import {
  Menu as MenuIcon,
  // AccountCircle, // substituído por Avatar com foto/iniciais
  Logout,
  Person,
  Brightness4,
  Brightness7,
  Search,
  HelpOutline,
  AutoAwesome,
  ReceiptLongOutlined,
  AdminPanelSettings,
} from "@mui/icons-material";

// import logo from "../assets/logo.png";
// import logowhite from "../assets/logowhite.png";
import { useBrand } from "../context/BrandContext";

import CompanySelector from "./CompanySelector";
import SearchAutocomplete from "./SearchAutocomplete";
import Tour from "./Tour"; // Componente de tour
import { useThemeContext } from "../context/ThemeContext";
import { useTranslatedThemeOptions } from "../hooks/useTranslatedThemeOptions";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  buildShellTourBundles,
  mergeShellAndPageSteps,
  filterStepsByExistingTargets,
  resolveTourRouteKey,
  getPageTourSteps,
} from "../tour/platformTour";
import { fetchTrainingHub } from "../api/client";
import { useTranslation } from "react-i18next";
import PlatformAiChatDialog from "./PlatformAiChatDialog";
import NotificationBell from "./notifications/NotificationBell";
import HelpOnboardingDialog from "./HelpOnboardingDialog";
import { useEffectivePermissions, toSet, can } from "../hooks/useEffectivePermissions";

/* ================= helpers de avatar ================= */
function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < String(string).length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = String(string).charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(fullname = "") {
  const s = String(fullname).trim();
  if (!s) return "US";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getUserAvatarUrl(user) {
  const u = user?.imagem;
  return typeof u === "string" && u.trim().length > 0 ? u : null;
}
/* ===================================================== */

const TopBar = ({ collapsed, setCollapsed, toggleDrawer }) => {
  const brand = useBrand();
  const { t } = useTranslation("common");

  const { logout, userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // Modal para busca e seleção de empresa no mobile
  const [aiOpen, setAiOpen] = useState(false);
  const [helpOnboardingOpen, setHelpOnboardingOpen] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false); // Ativação do tour
  const [tourSteps, setTourSteps] = useState([]);
  const [tourReady, setTourReady] = useState(false);
  const { changeTheme } = useThemeContext();
  const themeMenuOptions = useTranslatedThemeOptions();
  const theme = useTheme();

  const [themeAnchorEl, setThemeAnchorEl] = useState(null);
  const isThemeMenuOpen = Boolean(themeAnchorEl);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const openHelpOnboarding = useCallback(async () => {
    try {
      const list = await fetchTrainingHub();
      if (Array.isArray(list) && list.length > 0) {
        setHelpOnboardingOpen(true);
      } else {
        setIsTourActive(true);
      }
    } catch {
      setHelpOnboardingOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!isTourActive) {
      setTourSteps([]);
      setTourReady(false);
      return;
    }

    setTourReady(false);
    const apply = () => {
      const routeKey = resolveTourRouteKey(location.pathname);
      const pageRaw = getPageTourSteps(routeKey, location.pathname, isMobile);
      const page = filterStepsByExistingTargets(pageRaw);
      const omitMainIntro = page.some((s) => s.target === ".tour-main-content");
      const bundles = buildShellTourBundles(isMobile, { omitMainIntro });
      setTourSteps(mergeShellAndPageSteps(bundles, page));
      setTourReady(true);
    };

    apply();
    const t = setTimeout(apply, 350);
    return () => clearTimeout(t);
  }, [isTourActive, isMobile, location.pathname]);

  const handleThemeMenuOpen = (event) => setThemeAnchorEl(event.currentTarget);
  const handleThemeMenuClose = () => setThemeAnchorEl(null);
  const handleThemeChange = (t) => {
    changeTheme(t);
    setThemeAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);

  const isProfileMenuOpen = Boolean(profileAnchorEl);

  // Props do Avatar (foto ou iniciais)
  const avatarComputed = useMemo(() => {
    const name = userData?.nome || userData?.name || userData?.email || "Usuário";
    const url = getUserAvatarUrl(userData);
    if (url) {
      return {
        src: url,
        alt: name,
        sx: {
          width: 36,
          height: 36,
          border: `2px solid ${theme.palette.divider}`,
        },
        title: name,
      };
    }
    const bg = stringToColor(name);
    return {
      children: getInitials(name),
      alt: name,
      sx: {
        width: 36,
        height: 36,
        fontSize: 14,
        bgcolor: bg,
        color: theme.palette.getContrastText ? theme.palette.getContrastText(bg) : "#fff",
        border: `2px solid ${theme.palette.divider}`,
      },
      title: name,
    };
  }, [userData, theme.palette]);

  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const scopeSet = toSet(permData?.scopes);
  const canViewBilling = can(scopeSet, "billing", "view");
  const isMasterUser = Number(userData?.master) === 1;

  const avatarMenuComputed = useMemo(() => {
    // versão levemente maior para o header do menu
    const name = userData?.nome || userData?.name || userData?.email || "Usuário";
    const url = getUserAvatarUrl(userData);
    if (url) {
      return {
        src: url,
        alt: name,
        sx: {
          width: 40,
          height: 40,
        },
      };
    }
    const bg = stringToColor(name);
    return {
      children: getInitials(name),
      alt: name,
      sx: {
        width: 40,
        height: 40,
        fontSize: 16,
        bgcolor: bg,
        color: theme.palette.getContrastText ? theme.palette.getContrastText(bg) : "#fff",
      },
    };
  }, [userData, theme.palette]);

  // console.log('topoBar', status);

  return (
    <>
      <Tour
        key={location.pathname}
        steps={tourSteps}
        isTourActive={isTourActive && tourReady}
        onTourEnd={() => setIsTourActive(false)}
      />

      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          zIndex: 1201,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "0 10px" : "0 20px",
          }}
        >
          {/* Botão de Colapso */}
          <IconButton
            onClick={() => (isMobile ? toggleDrawer() : setCollapsed(!collapsed))}
            className="tour-menu"
            sx={{
              padding: 0,
              paddingRight: isMobile ? 2 : 3,
              color: theme.palette.text.primary,
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            className="tour-logo"
            sx={{ display: "flex", alignItems: "center", width: isMobile ? "120px" : "150px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <img
              src={theme.palette.mode === "dark" ? brand.assets.logo.dark : brand.assets.logo.light}
              alt={`${brand.name} Logo`}
              style={{ height: 40 }}
            />
          </Box>

          {/* Ícones para busca e seleção de empresa no mobile */}
          {isMobile && (
            <Tooltip title={t("topbar.searchCompanyTooltip")}>
              <IconButton
                className="tour-mobile-search-company"
                onClick={() => setModalOpen(true)}
                sx={{ color: theme.palette.text.primary }}
              >
                <Search />
              </IconButton>
            </Tooltip>
          )}

          {/* Campo de busca e seleção de empresas no desktop */}
          {!isMobile && (
            <>
              <Box
                className="tour-search"
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "10px",
                }}
              >
                <SearchAutocomplete />
              </Box>

              <Box
                className="tour-company"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  marginLeft: "16px",
                }}
              >
                <Box
                  sx={{
                    width: "250px",
                    maxWidth: "250px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <CompanySelector />
                </Box>
              </Box>
            </>
          )}

          <NotificationBell />

          {/* Botão de troca de tema */}
          <Tooltip title="Alterar Tema">
            <IconButton
              className="tour-theme"
              onClick={handleThemeMenuOpen}
              sx={{ marginLeft: "16px", color: theme.palette.text.primary }}
            >
              {theme.palette.mode === "dark" ? <Brightness7 /> : <Brightness4 />}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={themeAnchorEl}
            open={isThemeMenuOpen}
            onClose={handleThemeMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {themeMenuOptions.map((themeOption) => (
              <MenuItem key={themeOption.value} onClick={() => handleThemeChange(themeOption.value)}>
                {themeOption.label}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={t("topbar.platformAi")}>
            <IconButton
              onClick={() => setAiOpen(true)}
              className="tour-ai"
              sx={{ marginLeft: "8px", color: theme.palette.text.primary }}
            >
              <AutoAwesome />
            </IconButton>
          </Tooltip>

          {/* Seletor de Status */}
          {/* <StatusSelector
            selectedStatus={status} 
            onStatusChange={handleStatusChange}
          /> */}

          {/* Perfil do usuário */}
          <Box className="tour-profile" sx={{ display: "flex", alignItems: "center", marginLeft: "8px" }}>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
              {/* Avatar com foto (se existir) ou iniciais */}
              <Avatar {...avatarComputed} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Menu do perfil */}
      <Menu
        anchorEl={profileAnchorEl}
        open={isProfileMenuOpen}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 260,
            padding: 2,
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {/* Cabeçalho do Menu */}
        <Box display="flex" alignItems="center" gap={2} padding={1.5} borderBottom={`1px solid ${theme.palette.divider}`}>
          <Avatar {...avatarMenuComputed} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                maxWidth: "180px",
              }}
            >
              {userData?.nome}
            </Typography>
            <Tooltip title={userData?.email || ""}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                {userData?.email}
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Opções do Menu */}
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            navigate("/profile");
          }}
          sx={{ mt: 1 }}
        >
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <Typography>{t("topbar.myProfile")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            openHelpOnboarding();
          }}
          sx={{ mt: 0.5 }}
        >
          <ListItemIcon>
            <HelpOutline fontSize="small" />
          </ListItemIcon>
          <Typography>{t("topbar.helpOnboarding")}</Typography>
        </MenuItem>
        {!permsLoading && canViewBilling ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/settings/billing");
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <ReceiptLongOutlined fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.billingWorkspace")}</Typography>
          </MenuItem>
        ) : null}
        {!permsLoading && isMasterUser ? (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              navigate("/platform/xprobase");
            }}
            sx={{ mt: 0.5 }}
          >
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" />
            </ListItemIcon>
            <Typography>{t("topbar.platformXprobase")}</Typography>
          </MenuItem>
        ) : null}
        <MenuItem onClick={logout} sx={{ mt: 1 }}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography>{t("topbar.logout")}</Typography>
        </MenuItem>
      </Menu>

      {/* Modal de busca e seleção de empresa para mobile */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("topbar.searchCompanyDialogTitle")}</DialogTitle>
        <DialogContent>
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("topbar.searchClients")}
            </Typography>
            <SearchAutocomplete />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("topbar.selectCompany")}
            </Typography>
            <CompanySelector />
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="contained" onClick={() => setModalOpen(false)}>
              {t("close")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <PlatformAiChatDialog open={aiOpen} onClose={() => setAiOpen(false)} />
      <HelpOnboardingDialog
        open={helpOnboardingOpen}
        onClose={() => setHelpOnboardingOpen(false)}
        onStartTour={() => {
          setHelpOnboardingOpen(false);
          setIsTourActive(true);
        }}
      />
    </>
  );
};

export default TopBar;
